import React from 'react';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import tw from 'twin.macro';
import Button from '@teetsh/app/src/components/Buttons/Button';

interface Props {
  children: [React.ReactNode, React.ReactNode, React.ReactNode];
  className?: string;
  showBackButton?: boolean;
  onBackClick?: () => void;
  sideColumnWidth?: string;
  breakpoint?: '2xl' | 'xl' | 'lg';
}

function getBreakpointStyles(breakpoint: '2xl' | 'xl' | 'lg') {
  switch (breakpoint) {
    case '2xl':
      return {
        extraLarge: tw`hidden 2xl:grid 2xl:items-center min-w-0`,
        large: tw`flex flex-col 2xl:hidden min-w-0`,
      };
    case 'lg':
      return {
        extraLarge: tw`hidden lg:grid lg:items-center min-w-0`,
        large: tw`flex flex-col lg:hidden min-w-0`,
      };
    case 'xl':
    default:
      return {
        extraLarge: tw`hidden xl:grid xl:items-center min-w-0`,
        large: tw`flex flex-col xl:hidden min-w-0`,
      };
  }
}

export function ToolbarStructure(props: Props) {
  const {
    children,
    className,
    showBackButton,
    onBackClick,
    sideColumnWidth,
    breakpoint = 'xl',
  } = props;

  const breakpointStyles = getBreakpointStyles(breakpoint);

  return (
    <div
      className={className}
      tw="border-b border-neutral-quiet mx-2 lg:mx-4 min-h-0 shrink-0 min-w-0 relative pt-3 pb-2"
    >
      {showBackButton && (
        <Button
          onClick={onBackClick}
          minimal
          intent="none"
          tw="absolute top-0 left-0"
          icon={ArrowLeftIcon}
          aria-label="Retour"
        />
      )}
      <div
        css={[
          breakpointStyles.extraLarge,
          sideColumnWidth
            ? `grid-template-columns: ${sideColumnWidth} 1fr ${sideColumnWidth};`
            : tw`grid-cols-[1fr_2fr_1fr]`,
        ]}
        data-testid="toolbar-extra-large"
      >
        {/* Left section with back button */}
        <div tw="flex gap-2 items-center self-end shrink-0">{children[0]}</div>

        {/* Center section - centered in its grid cell */}
        <div
          tw="flex items-center justify-center"
          css={[showBackButton && tw`px-12`]}
        >
          {children[1]}
        </div>

        {/* Right section - aligned to end */}
        <div tw="flex items-center justify-end self-end shrink-0">
          {children[2]}
        </div>
      </div>

      <div css={breakpointStyles.large} data-testid="toolbar-large">
        {/* First row with back button and middle child */}
        <div tw="w-full flex items-center mb-1">
          <div
            tw="flex-1 flex justify-center min-w-0"
            css={[showBackButton && tw`px-12`]}
          >
            {children[1]}
          </div>
        </div>

        {/* Second row with first and third child */}
        <div tw="w-full flex justify-between items-end">
          <div>{children[0]}</div>
          <div tw="grow">{children[2]}</div>
        </div>
      </div>
    </div>
  );
}
